import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import api from '../../services/api';
import { Sezione } from './SezioneProcedura';
import { Campo, chiave, annunciaSchedaSalvata } from './CampiScheda';
import BottoneRefertoDaScheda from './BottoneRefertoDaScheda';
import { useSporco, useUscitaProtetta } from '../../utils/uscitaProtetta';
import '../../styles/SchedeProcedura.css';

/**
 * Le schede cliniche del medico, compilate dentro una procedura.
 *
 * COSA DISEGNA
 * Una scheda per riquadro - chiuso, come tutti gli altri riquadri della
 * procedura: si apre quello che si compila. Dentro, le sezioni della
 * scheda, e dentro quelle i campi.
 * Le sezioni ripetibili - «Prodotti somministrati» della IV Therapy - si
 * ripetono una volta per prodotto, e ogni ripetizione porta i propri
 * campi: e' cio' che tiene insieme «NAD» con «500 mg».
 *
 * DUE COSE CHE SI VEDONO SOLO QUI
 * 1. Un valore fuori dall'intervallo di riferimento si segnala, non si
 *    rifiuta: una pressione di 160 e' un dato vero e va salvata.
 *    Rifiutarla insegnerebbe a scrivere numeri finti.
 * 2. Le controindicazioni di un prodotto compaiono quando quel prodotto
 *    viene scelto. Sono scritte nel catalogo, accanto alla risposta: il
 *    gestionale le mostra, non le inventa.
 */

const SchedeProcedura = ({ visita }) => {
    const [schede, setSchede] = useState([]);
    const [valori, setValori] = useState({});
    const [righe, setRighe] = useState({});
    /* «Modificato» = diverso dall'istantanea presa al caricamento e a ogni
       salvataggio riuscito, che ricarica (utils/uscitaProtetta.js). */
    const [versione, setVersione] = useState(0);
    const sporco = useSporco({ valori, righe }, versione);
    useUscitaProtetta(sporco);
    // scheda_id -> id della compilazione di questa visita, se gia' salvata
    const [compilazioni, setCompilazioni] = useState({});
    const [scrivibile, setScrivibile] = useState(false);
    const [nascosta, setNascosta] = useState(false);
    const [salvando, setSalvando] = useState(null);
    const [errore, setErrore] = useState(null);
    const [salvata, setSalvata] = useState(null);

    const visitaId = visita?.id;

    const carica = useCallback(async () => {
        if (!visitaId) return;
        try {
            const r = await api.get(`/schede/compilazione/${visitaId}`);
            const d = r.data?.data || {};
            setSchede(d.schede || []);
            setValori(d.valori || {});
            setRighe(d.righe || {});
            setVersione((v) => v + 1);
            setCompilazioni(d.compilazioni || {});
            setScrivibile(d.scrivibile === true);
            setNascosta(d.riservata_nascosta === true);
            setErrore(null);
        } catch (e) {
            setErrore('Non riesco a leggere le schede');
        }
    }, [visitaId]);

    useEffect(() => { carica(); }, [carica]);

    const quanteRighe = useCallback((scheda) => Math.max(1, righe[scheda.id] || 1), [righe]);

    const cambia = (campoId, riga, v) => {
        setValori((precedenti) => ({ ...precedenti, [chiave(campoId, riga)]: v }));
        setSalvata(null);
    };

    const salva = async (scheda) => {
        setSalvando(scheda.id);
        setErrore(null);
        try {
            const voci = [];
            for (const sez of scheda.sezioni) {
                const n = sez.ripetibile ? quanteRighe(scheda) : 1;
                for (let riga = 1; riga <= n; riga++) {
                    for (const c of sez.campi) {
                        const k = chiave(c.campo_id, riga);
                        if (!(k in valori)) continue;
                        voci.push({ campo_id: c.campo_id, riga, valore: valori[k] });
                    }
                }
            }
            if (!voci.length) { setSalvando(null); return; }
            await api.put(`/schede/compilazione/${visitaId}/${scheda.id}`, { valori: voci });
            annunciaSchedaSalvata();
            setSalvata(scheda.id);
            await carica();
        } catch (e) {
            setErrore(e?.message || 'Salvataggio non riuscito');
        } finally {
            setSalvando(null);
        }
    };

    const aggiungiRiga = (scheda) =>
        setRighe((p) => ({ ...p, [scheda.id]: quanteRighe(scheda) + 1 }));

    const togliRiga = async (scheda, riga) => {
        if (!window.confirm(`Togliere la ripetizione ${riga}?`)) return;
        try {
            await api.delete(`/schede/compilazione/${visitaId}/${scheda.id}/riga/${riga}`);
            await carica();
            setRighe((p) => ({ ...p, [scheda.id]: Math.max(1, quanteRighe(scheda) - 1) }));
        } catch (e) {
            setErrore(e?.message || 'Non riesco a togliere la ripetizione');
        }
    };

    const vuoto = useMemo(() => !schede.length, [schede]);

    if (nascosta) return null;
    if (vuoto) return null;

    return (
        <>
            {errore && <p className="sp-errore">{errore}</p>}
            {schede.map((s) => (
                <Sezione key={s.id} title={s.nome}
                         right={s.area_nome ? <span className="sp-area">{s.area_nome}</span> : null}>
                    {/* Una scheda senza nessun campo e' un segnaposto: il
                        riquadro c'e' perche' si sappia che quella cosa va
                        registrata, ma i campi non sono stati ancora decisi.
                        Dirlo in chiaro evita che qualcuno la scambi per una
                        scheda rotta e la segnali come guasto. */}
                    {s.sezioni.every((z) => !z.campi.length) && (
                        <p className="sp-segnaposto">
                            Questa scheda non ha ancora campi: il riquadro è pronto,
                            il contenuto si compone dal <b>catalogo dei campi</b>.
                        </p>
                    )}

                    {s.sezioni.filter((z) => z.campi.length > 0).map((sez) => (
                        <div className="sp-sezione" key={sez.id}>
                            <div className="sp-sezione-titolo">{sez.titolo}</div>

                            {sez.ripetibile ? (
                                <>
                                    {Array.from({ length: quanteRighe(s) }, (_, i) => i + 1).map((riga) => (
                                        <div className="sp-ripetizione" key={riga}>
                                            <div className="sp-ripetizione-testa">
                                                <span>{sez.ripetibile_etichetta} {riga}</span>
                                                {scrivibile && quanteRighe(s) > 1 && (
                                                    <button type="button" onClick={() => togliRiga(s, riga)}
                                                            title="Togli questa ripetizione">
                                                        <Trash2 size={13} />
                                                    </button>
                                                )}
                                            </div>
                                            <div className="sp-campi">
                                                {sez.campi.map((c) => (
                                                    <Campo key={c.campo_id} campo={c} riga={riga}
                                                           valore={valori[chiave(c.campo_id, riga)]}
                                                           bloccato={!scrivibile}
                                                           onChange={(v) => cambia(c.campo_id, riga, v)} />
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                    {scrivibile && (
                                        <button type="button" className="sp-aggiungi"
                                                onClick={() => aggiungiRiga(s)}>
                                            <Plus size={13} aria-hidden="true" /> Aggiungi {sez.ripetibile_etichetta?.toLowerCase()}
                                        </button>
                                    )}
                                </>
                            ) : (
                                <div className="sp-campi">
                                    {sez.campi.map((c) => (
                                        <Campo key={c.campo_id} campo={c} riga={1}
                                               valore={valori[chiave(c.campo_id, 1)]}
                                               bloccato={!scrivibile}
                                               onChange={(v) => cambia(c.campo_id, 1, v)} />
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}

                    {scrivibile && s.sezioni.some((z) => z.campi.length > 0) && (
                        <div className="sp-azioni">
                            {salvata === s.id && <span className="sp-salvata">Salvata</span>}
                            {/* Solo dopo il primo salvataggio: prima la
                                compilazione non esiste e non c'e' niente da copiare. */}
                            <BottoneRefertoDaScheda compilazioneId={compilazioni[s.id]} disabilitato={salvando === s.id} />
                            <button type="button" onClick={() => salva(s)} disabled={salvando === s.id}>
                                {salvando === s.id ? 'Salvo…' : 'Salva la scheda'}
                            </button>
                        </div>
                    )}
                </Sezione>
            ))}
        </>
    );
};

export default SchedeProcedura;
